import React from "react";
import { TITAN_CONFIG } from "@/config_hover";
import { toKebabCase } from "@/common/util";
import TitanPlanSvg from "@/common/images/logos/titan_plan.svg";
import TitanPlanProSvg from "@/common/images/logos/titan_plan_pro.svg";
import TitanPlanPremiumSvg from "@/common/images/logos/titan_plan_premium.svg";
import TitanPlanUltraSvg from "@/common/images/logos/titan_plan_ultra.svg";

export {
  buildTitanPlans,
  highlightStorage,
  tierOf,
  titanPlanIcon,
  titanTierTitle,
}

const STORAGE_SIZE_REGEX = /\b((?:10|50|100) GB)\b/;

// Plans are stored server-side as "titan_pro"/"titan_premium"/"titan_ultra",
// while TITAN_CONFIG keys its copy on the bare tier. Same strip as
// HasTucowsMailDomain#titan_plan_type.
const tierOf = (serviceType) => String(serviceType).replace(/^titan_/, "");

const titanTierTitle = (serviceType) => {
  const tier = tierOf(serviceType);
  return tier.charAt(0).toUpperCase() + tier.slice(1);
};

/**
 * @param {string} line Four word string, e.g.: "100 GB email storage"
 * @return {string] Wrap storage sizes ("10 GB", "50 GB", "100 GB") in <b> tags.
 */
function highlightStorage(line) {
  return line.split(STORAGE_SIZE_REGEX).map((part, i) =>
    i % 2 === 1 ? <b key={i}>{part}</b> : part
  );
}

/**
 * Merge the plans the server sells (which own the plan id and the price) with
 * the marketing copy that only lives in the front end.
 *
 * A tier missing from either side is dropped rather than rendered half-built:
 * a plan with no copy has nothing to show, and copy with no plan id cannot be
 * added to the cart.
 *
 * @param titanPlans {Array} `Recurring::Plan.titan_plan_options`
 * @return {Array} in the server's sort order
 */
function buildTitanPlans(titanPlans = []) {
  return titanPlans
    .map((plan) => {
      const copy = TITAN_CONFIG.types.find((t) => t.type === tierOf(plan.service_type));
      if (!copy) return null;

      return {
        planId: plan.plan_id,
        serviceType: plan.service_type,
        // Cards are headed by the bare tier ("Pro"), not the fuller
        // "Pro Mailbox" / "Titan Pro" labels used in the cart.
        title: titanTierTitle(plan.service_type),
        description: copy.description,
        features: copy.features,
        tag: copy.tag,
        taglines: copy.taglines,
        price: plan.price
      };
    })
    .filter(Boolean);
}

function titanPlanIcon(serviceType) {
  let icon = TitanPlanSvg;

  switch (serviceType) {
    case "titan_pro":
      icon = TitanPlanProSvg;
      break;
    case "titan_premium":
      icon = TitanPlanPremiumSvg;
      break;
    case "titan_ultra":
      icon = TitanPlanUltraSvg;
      break;
    default:
      break;
  }

  return (
    <img
      src={icon}
      alt={serviceType}
      className={`titan-${toKebabCase(serviceType)}`}
    />
  );
}
